import { Drawer } from 'antd'
import styled from 'styled-components'
import { Text, Heading } from '@lmc/ui-kit/src/general'
import { Spinner } from '@lmc/ui-kit'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { Tag } from 'antd/lib'
import { AdministrationRecordDetailContent } from './AdministrationRecordDetailContent'
import { StockRecord } from '../../types'
import { getAdministrationStatusTranslationKey } from '../../utils/stockRecordUtils'
import Administered from '../../iconsV2/Administered'
import Discontinued from '../../iconsV2/Discontinued'
import Missed from '../../iconsV2/Missed'
import Paused from '../../iconsV2/Paused'
import NotScheduledOrNotNeeded from '../../iconsV2/NotScheduledOrNotNeeded'

type AdministrationRecordDrawerProps = {
  isVisible: boolean
  onClose: () => void
  record: StockRecord | undefined
  medicationName: string
  controlledDrug?: boolean
}

export const AdministrationRecordDrawer = ({
  isVisible,
  onClose,
  record,
  medicationName,
  controlledDrug
}: AdministrationRecordDrawerProps) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)
  const isLoading = false

  if (!record || record.type !== 'administration') return null

  const adminData = record.administration
  const administrationStatus = adminData?.administrationStatus || ''
  const statusTranslationKey = getAdministrationStatusTranslationKey(administrationStatus)
  const statusTitle = t(statusTranslationKey)

  // Get the appropriate icon based on administrationStatus, matching the Legend
  const getStatusIcon = () => {
    switch (statusTranslationKey) {
      case 'ADMINISTERED':
        return <Administered />
      case 'DISCONTINUED':
        return <Discontinued />
      case 'MISSED':
        return <Missed />
      case 'PAUSED':
        return <Paused />
      case 'NOT_SCHEDULED_OR_NOT_NEEDED':
        return <NotScheduledOrNotNeeded />
      default:
        return <Administered />
    }
  }

  return (
    <StyledDrawer
      title={
        <>
          {isLoading && (
            <LoadingWrapper>
              <Spinner />
            </LoadingWrapper>
          )}
          {!isLoading && record && (
            <TitleWrapper>
              <MedicationNameRow>
                <MedicationNameText>{medicationName}</MedicationNameText>
                {controlledDrug && <Tag color="red">{t('CD')}</Tag>}
              </MedicationNameRow>
              <TitleContainer>
                <IconWrapper>
                  {getStatusIcon()}
                </IconWrapper>
                <StyledTitleHeading level={4}>{statusTitle}</StyledTitleHeading>
              </TitleContainer>
            </TitleWrapper>
          )}
        </>
      }
      placement="right"
      onClose={onClose}
      open={isVisible}
      width={736}
    >
      {isLoading && (
        <LoadingWrapper>
          <Spinner />
        </LoadingWrapper>
      )}
      {!isLoading && record && (
        <DrawerContent>
          <TopContent>
            <DetailsHeading level={3}>{t('DETAILS')}</DetailsHeading>
          </TopContent>
          <AdministrationRecordDetailContent record={record} />
        </DrawerContent>
      )}
    </StyledDrawer>
  )
}

const StyledDrawer = styled(Drawer)`
  .ant-drawer-header {
    box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
  }

  .ant-drawer-header-title {
    flex-direction: row-reverse;
  }
`

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledTitleHeading = styled(Heading)`
  color: ${(props) => props.theme.color.neutral.color3};
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`

const DrawerContent = styled.div``

const MedicationNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const MedicationNameText = styled(Text)`
  color: ${(props) => props.theme.color.neutral.color5};
`

const TopContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 0 16px 0;
`

const DetailsHeading = styled(Heading)`
  color: ${({ theme }) => theme.color.neutral.color3};
  margin: 0;
`

